import { __awaiter, __decorate } from "tslib";
import { Component, ViewEncapsulation } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LocationStrategy } from '@angular/common';
import { SharedHttp } from '../../services/http-client.service';
import { WebsiteThemeService } from '../../services/website-theme.service';
import { LanguageService } from '../../services/language.service';
import { AuthService } from '../../services/auth.service';
import { depositOriginStateAfterStatement } from '../../services/deposit-origin';
/**
 * Deposit Origin Modal Component — the "Origin of funds" window of the deposit trigger. It opens
 * over the page the user is on (wallet or exchange) in one of three states: the statement of
 * origin (the deposit questionnaire, recorded with order_type_name 'deposit'), the proof of origin
 * (POST market/deposit/documentation, bound to the user and the week, not to an order), or the
 * proof sent and awaiting the compliance review. When the proof is owed too, the statement goes on
 * to it in the same window; when nothing is left, the window closes and the action that opened it
 * continues (data.onClear).
 */
let DepositOriginModalComponent = class DepositOriginModalComponent {
    constructor(sanitizer, httpClient, themeService, languageService, auth, location) {
        this.sanitizer = sanitizer;
        this.httpClient = httpClient;
        this.themeService = themeService;
        this.languageService = languageService;
        this.auth = auth;
        this.location = location;
        this.questionnaire = [];
        this.questions = [];
        this.checkedAnswers = {};
        this.otherAnswers = {};
        this.selectedFiles = [];
        this.fileError = false;
        this.fileSize = 2;
        this.maxFiles = 10;
        this.sending = false;
        this.errorMessage = '';
        this.borderRadiusSmaller = '';
        this.borderRadiusLarger = '';
        this.errorColor = '';
        this.secondaryColor = '';
        this.inputBackColor = '';
        // the checkbox mark is white on the page's main colour; the window opens outside any page wrapper, so it sets --main itself
        this.mainColor = '';
        this.lang = localStorage.getItem('language') ? localStorage.getItem('language') : 'sr';
        this.themeService.getTheme().subscribe((data) => {
            this.borderRadiusSmaller = this.themeService.getStyleAttribute('border_radius_smaller');
            this.borderRadiusLarger = this.themeService.getStyleAttribute('border_radius_larger');
            this.errorColor = this.themeService.getStyleAttribute('error_color');
            this.secondaryColor = this.themeService.getStyleAttribute('secondary_text_color');
            this.inputBackColor = this.themeService.getStyleAttribute('input_background_color');
            this.mainColor = this.themeService.getStyleAttribute('main_color');
        });
        location.onPopState(() => {
            this.close();
        });
    }
    ngOnInit() {
        this.state = this.data.state;
        if (this.state === 'statement') {
            this.loadQuestions();
        }
        this.languageSubscription = this.languageService.language.subscribe((language) => {
            this.lang = language;
            if (this.state === 'statement') {
                this.loadQuestions();
            }
        });
    }
    ngOnDestroy() {
        if (this.languageSubscription) {
            this.languageSubscription.unsubscribe();
        }
    }
    close() {
        this.modal.destroy();
    }
    loadQuestions() {
        this.httpClient.get('market/order/questionnaire?order_type_name=deposit&language_name=' + this.lang).subscribe((data) => {
            this.questionnaire = data[0];
            this.questions = this.questionnaire[0].questionnaire.questions;
        });
    }
    /** Every question needs an answer, a customizable option its text (at most 254 characters); the same rules as the questionnaire page. */
    buildAnswers() {
        const answers = Array();
        for (const question of this.questions) {
            const optionsAnswers = Array();
            for (const key of Object.keys(this.checkedAnswers)) {
                const [questionId, optionId] = key.split('.').map(Number);
                if (!this.checkedAnswers[key] || questionId !== question.id) {
                    continue;
                }
                const option = question.question_options.find((questionOption) => questionOption.id === optionId);
                if (option && option.customizable) {
                    if (!this.otherAnswers[key]) {
                        this.errorMessage = 'QUESTIONNAIRE.TEXTAREA_ERROR';
                        return null;
                    }
                    if (this.otherAnswers[key].length > 254) {
                        this.errorMessage = 'QUESTIONNAIRE.TEXTAREA_LENGTH';
                        return null;
                    }
                }
                optionsAnswers.push({
                    question_option_id: optionId,
                    customizable_value: this.otherAnswers[key] !== undefined ? this.otherAnswers[key] : null
                });
            }
            if (optionsAnswers.length === 0) {
                this.errorMessage = 'QUESTIONNAIRE.ANSWER_ERROR';
                return null;
            }
            answers.push({ question_id: question.id, options_answers: optionsAnswers });
        }
        return answers;
    }
    sendStatement() {
        this.errorMessage = '';
        const answers = this.buildAnswers();
        if (answers === null) {
            return;
        }
        this.sending = true;
        const statement = {
            questionnaire_id: this.questionnaire[0].questionnaire_id,
            answers,
            order_type_name: 'deposit'
        };
        this.httpClient.post('market/order/questionnaire', statement).subscribe((success) => {
            this.afterStatement();
        }, (error) => {
            // the week's statement is already recorded (sent from another device or tab)
            if (error.error && error.error.message === 'QUESTIONNAIRE_ALREADY_SUBMITTED') {
                this.afterStatement();
                return;
            }
            this.sending = false;
            this.errorMessage = 'ERROR.TRANSACTION_GENERIC_MESSAGE';
        });
    }
    /** Read the flags fresh: on to the proof (or its pending state) when it is owed too, else close and let the action continue. */
    afterStatement() {
        this.auth.fetchUser().then(() => {
            this.sending = false;
            const next = depositOriginStateAfterStatement(JSON.parse(localStorage.getItem('user')));
            if (next === null) {
                this.close();
                if (this.data.onClear) {
                    this.data.onClear();
                }
                return;
            }
            this.state = next;
        });
    }
    sendProof() {
        return __awaiter(this, void 0, void 0, function* () {
            this.sending = true;
            this.errorMessage = '';
            const documentationFiles = [];
            for (const file of this.selectedFiles) {
                documentationFiles.push({ name: file.name, content: yield this.readAsDataUrl(file) });
            }
            this.httpClient.post('market/deposit/documentation', { documentation_files: documentationFiles }).subscribe((success) => {
                this.sending = false;
                this.state = 'pending';
                // the rest of the app reads the flags from the cached user
                this.auth.fetchUser();
            }, (error) => {
                this.sending = false;
                this.errorMessage = 'DEPOSIT_ORIGIN.PROOF_UPLOAD_FAILED';
            });
        });
    }
    readAsDataUrl(file) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
        });
    }
    fileChange(event) {
        const filesToUpload = Array.prototype.slice.call(event.target.files);
        for (const fileToUpload of filesToUpload) {
            if (!this.selectedFiles.some((selected) => selected.name === fileToUpload.name)) {
                this.selectedFiles = this.selectedFiles.concat(fileToUpload);
            }
        }
        event.target.value = '';
        this.validateFiles();
    }
    removeFile(index) {
        this.selectedFiles.splice(index, 1);
        this.validateFiles();
    }
    validateFiles() {
        const allowedExtensions = ['jpg', 'png', 'jpeg', 'pdf', 'gif'];
        this.fileError = this.selectedFiles.some((file) => file.size > 1024 * 1024 * this.fileSize || !allowedExtensions.includes(file.name.split('.').pop().toLowerCase()));
    }
    roundNumber(num, places) {
        return Math.round(num * Math.pow(10, places)) / Math.pow(10, places);
    }
};
DepositOriginModalComponent.ctorParameters = () => [
    { type: DomSanitizer },
    { type: SharedHttp },
    { type: WebsiteThemeService },
    { type: LanguageService },
    { type: AuthService },
    { type: LocationStrategy }
];
DepositOriginModalComponent = __decorate([
    Component({
        selector: 'ecd-deposit-origin-modal',
        encapsulation: ViewEncapsulation.None,
        template: __importDefault(require("./deposit-origin-modal.component.html")).default
    })
], DepositOriginModalComponent);
export { DepositOriginModalComponent };
